import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { Detail } from '@tecuity/granite-ui/Detail'
import { ErrorMessage } from './PaymentModal'
import PayorSearchModalContent from './PayorSearchModalContent'
import * as api from '../../../requests'

const frequentPayorLabel = process.env.FREQUENT_PAYOR_LABEL

const ChargeModal = ({ charge = { Quantity: 1 }, clickUpdate, closeModal }) => {
  const { receiptItemTypes, currentUser /*redirectBackTo*/ } = useSelector(
    (state) => state.receipt,
    shallowEqual
  )
  const [addEditCharge, setAddEditCharge] = useState({ Quantity: 1 })
  const [depositToAccountName, setDepositToAccountName] = useState('')
  const [showPayorSearch, setShowPayorSearch] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    //DepositToAccountReceiptItemTypeName
    api.GET_APP_SETTING_BY_NAME(
      'DepositToAccountReceiptItemTypeName',
      (res) => {
        // console.log(res)
        setDepositToAccountName(res.VALUE)
      },
      console.error
    )
  }, [])

  useEffect(() => {
    setAddEditCharge({ ...charge })
  }, [charge])

  const total = useMemo(() => {
    if (addEditCharge.Quantity && addEditCharge.Quantity > 0 && addEditCharge.Amount)
      return (addEditCharge.Quantity * addEditCharge.Amount)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return ''
  }, [addEditCharge])

  const chargeItemTypes = useMemo(() => {
    // console.log(receiptItemTypes);
    const { Charges = [] } = receiptItemTypes
    // if (redirectBackTo.includes('correspondence')) {
    //   return [...Charges.filter((ty) => ty.ReceiptItemTypeName.includes('Refund')).map((type) => {
    //     return {
    //       value: type.ReceiptItemTypeID.toString(),
    //       label: type.ReceiptItemTypeName,
    //     }
    //   })];
    // }
    return [
      ...Charges.map((type) => {
        return {
          value: type.ReceiptItemTypeId.toString(),
          label: type.Name,
        }
      }),
    ]
  }, [receiptItemTypes])

  const setChargeType = (typeId) => {
    const type = chargeItemTypes.find((t) => t.value === typeId)
    if (type)
      setAddEditCharge({
        ...addEditCharge,
        Type: { Description: type.label, ReceiptItemTypeId: type.value },
      })
  }

  const chargeTypeMissing = useMemo(() => {
    return !addEditCharge.Type || !addEditCharge.Type.ReceiptItemTypeId
  }, [addEditCharge])

  const chargeTypeMessage = useMemo(() => {
    if (showErrors && chargeTypeMissing) return 'Required Field'
    return undefined
  }, [chargeTypeMissing, showErrors])

  const missingDln = useMemo(() => {
    return !addEditCharge.DLNNum
  }, [addEditCharge])

  const dlnFormatted = useMemo(() => {
    if (missingDln) return true

    const regex = /^[A-Z]\d{4}-\d{4}$/ //B1234-1234
    return addEditCharge.DLNNum.match(regex) !== null
  }, [addEditCharge, missingDln])

  const dlnMessage = useMemo(() => {
    if (showErrors && !missingDln && !dlnFormatted)
      return 'Invalid DLN. Must be in format: B1234-1234'
    return undefined
  }, [showErrors, missingDln, dlnFormatted])

  const canSearchFrequentPayor = useMemo(() => {
    if (!depositToAccountName) return false
    return !chargeTypeMissing && addEditCharge.Type.Description === depositToAccountName
  }, [chargeTypeMissing, addEditCharge, depositToAccountName])

  const missingFrequentPayor = useMemo(() => {
    return !addEditCharge.Payor || !addEditCharge.Payor.FrequentPayorNum
  }, [addEditCharge])

  const descriptionMessage = useMemo(() => {
    if (showErrors && canSearchFrequentPayor && missingFrequentPayor)
      return `${frequentPayorLabel || `Frequent Payor`} must be selected for this Charge Type`
    return undefined
  }, [missingFrequentPayor, canSearchFrequentPayor, showErrors])

  const quantityMissing = useMemo(() => {
    return !addEditCharge.Quantity
  }, [addEditCharge])

  const quantityMessage = useMemo(() => {
    if (showErrors && quantityMissing) return 'Required Field'
    return undefined
  }, [showErrors, quantityMissing])

  const amountMissing = useMemo(() => {
    return !addEditCharge.Amount
  }, [addEditCharge])

  const amountMessage = useMemo(() => {
    if (showErrors && amountMissing) return 'Required Field'
    return undefined
  }, [showErrors, amountMissing])

  const validationErrors = useMemo(() => {
    let errors = []

    if (
      chargeTypeMissing ||
      quantityMissing ||
      amountMissing ||
      (canSearchFrequentPayor && missingFrequentPayor)
    )
      errors.push('Missing Required Fields')

    if (!dlnFormatted) errors.push('Invalid DLN')

    if (errors.length === 0) setShowErrors(false)
    return errors
  }, [
    chargeTypeMissing,
    quantityMissing,
    amountMissing,
    missingFrequentPayor,
    canSearchFrequentPayor,
    dlnFormatted,
  ])

  const clickSaveCharge = () => {
    if (validationErrors.length === 0) {
      clickUpdate({ ...addEditCharge, LastModifiedBy: currentUser.App_User_ID })
    } else {
      setShowErrors(true)
    }
  }

  const selectPayorAccount = (account) => {
    setShowPayorSearch(false)
    const addr = account.ContactAddress
    const desc = `${account.FrequentPayorName ? account.FrequentPayorName.trim() : ``}${
      addr.AddressLine1 ? ` ${addr.AddressLine1.trim()},` : ``
    }${addr.City ? ` ${addr.City.trim()}` : ``}${
      addr.StateOrProvince ? ` ${addr.StateOrProvince.trim()}` : ``
    }`
    const payor = {
      // Do NOT seed the payor-snapshot ID with the frequent-payor ID — they are
      // distinct keys (PAYOR_ID vs FREQUENT_PAYOR_ID). The frequent-payor link is
      // carried by FrequentPayorID below; leave ID = 0 so the backend mints a fresh
      // PAYOR snapshot from the name/address rather than persisting a bad PAYOR_ID FK.
      ID: 0,
      Name: account.FrequentPayorName,
      Address: addr.AddressLine1,
      Address2: addr.AddressLine2,
      Address3: addr.AddressLine3,
      City: addr.City,
      State: addr.StateOrProvince,
      Zip: addr.PostalCode,
      Country: addr.Country,
      Phone: '',
      FrequentPayorNum: account.FrequentPayorNum,
    }
    setAddEditCharge({
      ...addEditCharge,
      FrequentPayorID: account.FrequentPayorId,
      RefCheckNum: account.FrequentPayorNum,
      Payor: payor,
      Description: desc,
    })
  }

  return (
    <React.Fragment>
      {!showPayorSearch && (
        <div className="receipt-modal">
          <React.Fragment>
            <div className="fields">
              <SelectBox
                label={
                  <span>
                    <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Charge Type
                  </span>
                }
                value={addEditCharge.Type ? addEditCharge.Type.ReceiptItemTypeId : ''}
                options={chargeItemTypes}
                onChange={setChargeType}
                valid={chargeTypeMessage ? false : undefined}
                errorMessage={chargeTypeMessage}
              />
              <TextInput
                label="Reference #"
                value={addEditCharge.RefCheckNum || ''}
                onChange={(e) =>
                  setAddEditCharge({ ...addEditCharge, RefCheckNum: e.target.value })
                }
              />
              <TextInput
                label="DLN #"
                value={addEditCharge.DLNNum || ''}
                onChange={(e) => setAddEditCharge({ ...addEditCharge, DLNNum: e.target.value })}
                valid={dlnMessage ? false : undefined}
                errorMessage={dlnMessage}
              />
              <TextInput
                label={
                  <span>
                    {canSearchFrequentPayor && (
                      <span style={{ color: `hsl(345,100%,42%)` }}>*</span>
                    )}
                    Description
                  </span>
                }
                value={addEditCharge.Description || ''}
                onChange={(e) =>
                  setAddEditCharge({
                    ...addEditCharge,
                    Description: e.target.value,
                    Payor: undefined,
                  })
                }
                disabled={canSearchFrequentPayor ? true : null}
                valid={descriptionMessage ? false : undefined}
                errorMessage={descriptionMessage}
              />
              <div className="receipt-currency">
                <TextInput
                  label={
                    <span>
                      <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Quantity
                    </span>
                  }
                  value={addEditCharge.Quantity || ''}
                  onChange={(e) => setAddEditCharge({ ...addEditCharge, Quantity: e.target.value })}
                  valid={quantityMessage ? false : undefined}
                  errorMessage={quantityMessage}
                />
                <TextInput
                  label={
                    <span>
                      <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Amount
                    </span>
                  }
                  value={addEditCharge.Amount || ''}
                  onChange={(e) => setAddEditCharge({ ...addEditCharge, Amount: e.target.value })}
                  valid={amountMessage ? false : undefined}
                  errorMessage={amountMessage}
                />
                <Detail label="Total" children={`$${total}`} fontSize="large" />
              </div>
            </div>
            {showErrors && (
              <div className="error-messages">
                {validationErrors.map((err, index) => {
                  return <ErrorMessage key={index} message={err} />
                })}
              </div>
            )}
          </React.Fragment>

          <ContextBar elevation="standard" className="modal-context-bar">
            {showPayorSearch ? null : (
              <div className="context-bar-content context-button-wrapper">
                <ContextButton variant="secondary" onClick={closeModal}>
                  Cancel
                </ContextButton>
                {canSearchFrequentPayor && (
                  <ContextButton
                    variant={'warning'}
                    onClick={() => setShowPayorSearch(true)}
                    customCss={''}
                  >
                    Search {frequentPayorLabel || 'Frequent Payor'}
                  </ContextButton>
                )}
                <ContextButton variant="primary" onClick={() => clickSaveCharge()}>
                  Save
                </ContextButton>
              </div>
            )}
          </ContextBar>
        </div>
      )}
      {showPayorSearch && (
        <PayorSearchModalContent
          setShowPayorSearch={setShowPayorSearch}
          selectPayorAccount={selectPayorAccount}
        />
      )}
    </React.Fragment>
  )
}

export default ChargeModal
